import { FileText } from 'lucide-react'
import type { Document } from '@agentwiki/shared'
import { cn } from '../../lib/utils'
import type { RoutableDocument } from '../../hooks/use-open-document-route'
import { BrowsePanel, type BrowseFilter } from '../sidebar/browse-panel'
import { FolderTree } from '../sidebar/folder-tree'
import { RecentDocuments } from '../sidebar/recent-documents'
import { SortControls } from '../sidebar/sort-controls'
import { DocumentStatusBadge } from '../shared/document-status-badge'
import { ErrorBoundary } from './error-boundary'

interface ContextualSidebarContentProps {
  isDark: boolean
  isMobile: boolean
  search: string
  showBrowse: boolean
  browseFilter: BrowseFilter | null
  filteredDocuments: Document[]
  sortMode: 'manual' | 'name' | 'date'
  sortDirection: 'asc' | 'desc'
  onBrowseFilterChange: (filter: BrowseFilter | null) => void
  onSortChange: (mode: 'manual' | 'name' | 'date', direction: 'asc' | 'desc') => void
  onDocumentOpen: (document: RoutableDocument) => void
  onMobileDocumentOpen: () => void
  onErrorReset: () => void
}

export function ContextualSidebarContent({
  isDark,
  isMobile,
  search,
  showBrowse,
  browseFilter,
  filteredDocuments,
  sortMode,
  sortDirection,
  onBrowseFilterChange,
  onSortChange,
  onDocumentOpen,
  onMobileDocumentOpen,
  onErrorReset,
}: ContextualSidebarContentProps) {
  return (
    <>
      {showBrowse && (
        <div className={cn('border-b pb-2', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
          <BrowsePanel
            activeFilter={browseFilter}
            onSelectFilter={onBrowseFilterChange}
            onClearFilter={() => onBrowseFilterChange(null)}
          />
        </div>
      )}

      {!browseFilter && (
        <SortControls mode={sortMode} direction={sortDirection} onChange={onSortChange} />
      )}
      {!browseFilter && !search && (
        <RecentDocuments onDocumentOpen={isMobile ? onMobileDocumentOpen : undefined} />
      )}

      <div className="flex-1 overflow-y-auto px-1 py-1">
        {browseFilter ? (
          <FilteredDocumentList
            documents={filteredDocuments}
            isDark={isDark}
            onDocumentOpen={onDocumentOpen}
          />
        ) : (
          <ErrorBoundary onReset={onErrorReset}>
            <FolderTree
              searchQuery={search}
              sortMode={sortMode}
              sortDirection={sortDirection}
              onDocumentOpen={isMobile ? onMobileDocumentOpen : undefined}
            />
          </ErrorBoundary>
        )}
      </div>
    </>
  )
}

function FilteredDocumentList({
  documents,
  isDark,
  onDocumentOpen,
}: {
  documents: Document[]
  isDark: boolean
  onDocumentOpen: (document: RoutableDocument) => void
}) {
  if (documents.length === 0) {
    return (
      <p
        className={cn(
          'px-2 py-4 text-center text-xs',
          isDark ? 'text-neutral-600' : 'text-neutral-400',
        )}
      >
        No documents found
      </p>
    )
  }

  return (
    <div className="space-y-0.5">
      {documents.map((document) => (
        <button
          key={document.id}
          type="button"
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-2 py-2.5 text-left text-sm md:py-1 md:text-xs',
            isDark
              ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200 active:bg-surface-3'
              : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800 active:bg-neutral-100',
          )}
          onClick={() => onDocumentOpen(document)}
        >
          <FileText
            className="h-4 w-4 shrink-0 text-neutral-500 md:h-3.5 md:w-3.5"
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 truncate">{document.title}</span>
          <DocumentStatusBadge doc={document} compact className="hidden xl:inline-flex" />
        </button>
      ))}
    </div>
  )
}
